import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, ExternalLink, Star, Package, ShoppingBag, Book, Apple, Activity, RefreshCw, Heart } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { shopAPI, seedAPI } from '../services/api';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await shopAPI.getAll(null, 100);
      if (data.length === 0) {
        // Seed products if none exist
        await seedAPI.seedProducts();
        const seededData = await shopAPI.getAll(null, 100);
        setProducts(seededData);
      } else {
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleViewOnAmazon = (product) => {
    window.open(product.amazon_link, '_blank');
    toast({
      title: "Opening Amazon",
      description: `Viewing ${product.name} on Amazon`
    });
  };

  const handleAddToWishlist = (product) => {
    toast({
      title: "Added to Wishlist",
      description: `${product.name} saved for later`
    });
  };

  const categories = [
    { name: 'All', icon: ShoppingBag },
    { name: 'Monitors', icon: Activity },
    { name: 'Test Strips', icon: Package },
    { name: 'Lancets', icon: Package },
    { name: 'Insulin Supplies', icon: Package },
    { name: 'Diabetic Food', icon: Apple },
    { name: 'Supplements', icon: Apple },
    { name: 'Health Monitors', icon: Activity },
    { name: 'Accessories', icon: Package },
    { name: 'Emergency Supplies', icon: Activity },
    { name: 'Kitchen Tools', icon: Package }
  ];

  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const ProductCard = ({ product }) => (
    <Card className="shadow-md border-0 hover:shadow-xl transition-all overflow-hidden dark:bg-gray-800">
      <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-700">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-4 hover:scale-110 transition-transform duration-300"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=300&h=200&fit=crop';
          }}
        />
        <div className="absolute top-3 right-3">
          <Badge className="bg-orange-500 text-white">
            <Star className="w-3 h-3 mr-1 fill-current" />
            {product.rating}
          </Badge>
        </div>
        <div className="absolute top-3 left-3">
          <Badge variant="secondary" className="bg-white/90 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {product.category}
          </Badge>
        </div>
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg line-clamp-2 dark:text-white">{product.name}</CardTitle>
        <CardDescription className="line-clamp-2 dark:text-gray-400">{product.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-bold text-gray-900 dark:text-white">${product.price}</span>
          <span className="text-sm text-gray-600 dark:text-gray-400">on Amazon</span>
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {product.reviews?.toLocaleString()} reviews
        </div>

        {product.features && product.features.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">Key Features:</p>
            <div className="space-y-1">
              {product.features.slice(0, 3).map((feature, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => handleViewOnAmazon(product)}
            className="flex-1 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View on Amazon
          </Button>
          <Button
            onClick={() => handleAddToWishlist(product)}
            variant="outline"
            size="icon"
            className="dark:border-gray-600"
          >
            <Heart className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Diabetes Shop</h2>
              <p className="text-orange-100 text-lg">Amazon Best-Seller Products</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={fetchProducts}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="shadow-md border-0 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 dark:bg-gray-800">
        <CardContent className="py-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Shop with Confidence</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We've curated the highest-rated diabetes products from Amazon. All products are top-rated 
                by thousands of real customers. Click "View on Amazon" to see full details, read reviews, 
                and purchase securely with Prime shipping available.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.name;
          const count = category.name === 'All' 
            ? products.length 
            : products.filter(p => p.category === category.name).length;
          
          return (
            <Button
              key={category.name}
              variant={isActive ? "default" : "outline"}
              onClick={() => setActiveCategory(category.name)}
              className={isActive 
                ? "bg-orange-500 hover:bg-orange-600" 
                : "dark:border-gray-600 dark:text-gray-300"
              }
            >
              <Icon className="w-4 h-4 mr-2" />
              {category.name}
              <Badge variant="secondary" className="ml-2 bg-white/20">
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Products Grid */}
      <div>
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {activeCategory === 'All' ? 'All Products' : activeCategory}
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
              ({filteredProducts.length} products)
            </span>
          </h3>
        </div>
        
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No products found in this category</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>

      {/* Popular Bundles */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Popular Product Bundles</CardTitle>
          <CardDescription className="dark:text-gray-400">Save when buying these items together</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Complete Glucose Monitoring Kit</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Contour Next ONE Meter + 100 Test Strips + Lancets
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">$62.97</span>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => window.open('https://www.amazon.com', '_blank')}
                >
                  View Bundle
                </Button>
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Travel Essentials Pack</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                FRIO Cooling Case + Glucose Meter + Emergency Glucose
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">$59.93</span>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.open('https://www.amazon.com', '_blank')}
                >
                  View Bundle
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Amazon Disclaimer */}
      <Card className="shadow-md border-0 bg-gray-50 dark:bg-gray-800">
        <CardContent className="py-4">
          <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>Note:</strong> DiaWise is a participant in the Amazon Services LLC Associates Program. 
              When you click "View on Amazon" and make a purchase, we may earn a small commission at no extra cost to you. 
              This helps us maintain and improve DiaWise. Prices and availability are subject to change on Amazon.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Shop;
